import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Buildings, Plus, PencilSimple, MapPin, Users, FileText, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const EMPTY = { code: "", name: "", city: "", address: "", manager_name: "", contact: "", status: "Active" };

export default function Branches() {
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); loadKpi(); }, []);

  const load = () => api.get("/branches").then((r) => setRows(r.data)).catch(() => {});
  const loadKpi = () => api.get("/branches/consolidated").then((r) => setKpi(r.data)).catch(() => setKpi([]));

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and Name required"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/branches/${editing.id}`, form);
      else await api.post("/branches", form);
      toast.success("Branch saved");
      setOpen(false); await load(); await loadKpi();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-cyan-600 font-semibold mb-2 flex items-center gap-2">
            <Buildings size={14} weight="fill" /> Multi-Location
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Branches</h1>
          <p className="text-sm text-slate-600 mt-1.5">Every record — customer, application, expense — belongs to a branch. Users only see data from their own branch.</p>
        </div>
        <Button onClick={openNew} data-testid="br-new-btn" className="gap-2 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Plus size={16} weight="bold" /> New Branch
        </Button>
      </div>

      {/* Consolidated KPI (super admin view) */}
      {kpi.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="br-consolidated">
          {kpi.map((b) => (
            <Card key={b.id} className="p-4 bg-white border-l-4 border-l-cyan-500">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{b.code}</div>
                  <div className="font-semibold text-slate-900">{b.name}</div>
                </div>
                <Badge variant="outline" className={b.status === "Active" ? "border-emerald-300 text-emerald-700" : ""}>{b.status}</Badge>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mb-3"><MapPin size={11} weight="fill" /> {b.city || "—"}</div>
              <div className="grid grid-cols-3 gap-1 text-center pt-2 border-t border-slate-100">
                <MiniStat icon={Users} label="Customers" value={b.customers} />
                <MiniStat icon={FileText} label="Apps" value={b.applications} />
                <MiniStat icon={CurrencyInr} label="Revenue" value={inr(b.revenue)} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Branch table */}
      <Card className="p-4 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3">Code</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">City</th>
                <th className="text-left py-2 pr-3">Manager</th>
                <th className="text-left py-2 pr-3">Contact</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-right py-2 pr-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-3 font-mono text-xs text-cyan-700">{r.code}</td>
                  <td className="py-2 pr-3 font-medium">{r.name}</td>
                  <td className="py-2 pr-3">{r.city || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-3">{r.manager_name || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-3">{r.contact || <span className="text-slate-300">—</span>}</td>
                  <td className="py-2 pr-3">
                    <Badge variant="outline" className={r.status === "Active" ? "border-emerald-300 text-emerald-700" : "text-slate-500"}>{r.status}</Badge>
                  </td>
                  <td className="py-2 pr-1 text-right">
                    <button onClick={() => openEdit(r)} data-testid={`br-edit-${r.code}`} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded"><PencilSimple size={14} weight="bold" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : "New Branch"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BR002" disabled={!!editing} data-testid="br-code" /></Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 rounded border border-slate-200 px-3 text-sm">
                <option>Active</option><option>Inactive</option>
              </select>
            </Field>
            <Field label="Name *" full><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Wagle Estate" data-testid="br-name" /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="Manager"><Input value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} /></Field>
            <Field label="Contact" full><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Phone / email" /></Field>
            <Field label="Address" full><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="br-save-btn">
              {saving ? "Saving…" : editing ? "Update" : "Create Branch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
      {children}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div>
      <Icon size={14} className="mx-auto text-slate-400" weight="fill" />
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value}</div>
    </div>
  );
}
